import { useDeferredValue, useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  FolderOpen,
  Instagram,
  Loader2,
  RefreshCw,
  Save,
  Settings2,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import type {
  ColecaoDisponivel,
  ContaSeguida,
  InstagramSessao,
} from "@base/contracts";
import {
  Botao,
  CampoDeTexto,
  EstadoDeErro,
  EstadoVazio,
  Esqueleto,
  Etiqueta,
  cn,
} from "@base/ui";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import {
  useAtualizarColecoesDoInstagram,
  useAtualizarContasSeguidas,
  useConectarInstagram,
  useConfirmar2faInstagram,
  useImportarSessaoInstagram,
  useInvalidarSessaoInstagram,
  useListarColecoesDisponiveis,
  useListarContasSeguidas,
  useSalvarColecoesSelecionadas,
  useSalvarContasStoriesSelecionadas,
  useAtualizarClickUp,
  useStatusIntegracoes,
  useStatusSessaoInstagram,
  useStatusSincronizacao,
  useValidarSessaoInstagram,
} from "../application/use-referencias";
import { sessaoInstagramDoCookie } from "../application/sessao-instagram-do-cookie";
import { limparCacheColecoesDisponiveis } from "../infrastructure/cache-colecoes-disponiveis";
import { MidiaAutenticada } from "./midia-autenticada";

export type AbaIntegracaoDaConta = "instagram" | "clickup";

type ItemDeAba = {
  readonly id: AbaIntegracaoDaConta;
  readonly rotulo: string;
  readonly descricao: string;
  readonly Icone: typeof Instagram;
};

/**
 * Integrações da conta do usuário (não do projeto).
 */
const ITENS_INTEGRACAO: readonly ItemDeAba[] = [
  {
    id: "instagram",
    rotulo: "Instagram (estúdio)",
    descricao: "Coleções e stories. Não publica posts.",
    Icone: Instagram,
  },
  {
    id: "clickup",
    rotulo: "ClickUp",
    descricao: "Token e lista da sua conta",
    Icone: Settings2,
  },
];

function lerAbaIntegracaoDoHash(): AbaIntegracaoDaConta {
  const query = window.location.hash.split("?")[1] ?? "";
  const aba = new URLSearchParams(query).get("aba");
  if (aba === "instagram" || aba === "clickup") return aba;
  return "instagram";
}

function escreverAbaIntegracaoNoHash(aba: AbaIntegracaoDaConta): void {
  window.location.hash = `perfil?aba=${aba}`;
}

/**
 * Painel de integrações da conta (Instagram do estúdio + ClickUp).
 * Hospedado em Configurações da conta (#perfil).
 */
export function ConteudoIntegracoesDaConta({
  abaControlada,
  onMudarAba,
  esconderNavegacao = false,
}: {
  readonly abaControlada?: AbaIntegracaoDaConta;
  readonly onMudarAba?: (aba: AbaIntegracaoDaConta) => void;
  readonly esconderNavegacao?: boolean;
}) {
  const [abaInterna, definirAbaInterna] = useState<AbaIntegracaoDaConta>(() =>
    lerAbaIntegracaoDoHash(),
  );
  const aba = abaControlada ?? abaInterna;

  useEffect(() => {
    if (abaControlada !== undefined) return;
    const sync = () => definirAbaInterna(lerAbaIntegracaoDoHash());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [abaControlada]);

  const selecionarAba = (id: AbaIntegracaoDaConta) => {
    if (onMudarAba) onMudarAba(id);
    else {
      definirAbaInterna(id);
      escreverAbaIntegracaoNoHash(id);
    }
  };

  const sessaoInstagram = useStatusSessaoInstagram();
  const instagramConectado =
    Boolean(sessaoInstagram.data?.username?.trim()) &&
    sessaoInstagram.data?.status === "valida";
  const disponiveis = useListarColecoesDisponiveis(instagramConectado);
  const atualizarColecoes = useAtualizarColecoesDoInstagram();
  const salvar = useSalvarColecoesSelecionadas();
  const contasSeguidas = useListarContasSeguidas(instagramConectado);
  const atualizarSeguidos = useAtualizarContasSeguidas();
  const salvarStories = useSalvarContasStoriesSelecionadas();
  const sincronizacao = useStatusSincronizacao();
  const integracoes = useStatusIntegracoes();
  const atualizarClickUp = useAtualizarClickUp();
  const validarSessao = useValidarSessaoInstagram();
  const invalidarSessao = useInvalidarSessaoInstagram();
  const conectar = useConectarInstagram();
  const confirmar2fa = useConfirmar2faInstagram();
  const [ids, definirIds] = useState<Set<string>>(new Set());
  const [hidratado, definirHidratado] = useState(false);
  const [sujoLocal, definirSujoLocal] = useState(false);
  const [idsStories, definirIdsStories] = useState<Set<string>>(new Set());
  const [hidratadoStories, definirHidratadoStories] = useState(false);
  const [sujoStoriesLocal, definirSujoStoriesLocal] = useState(false);
  const [buscaStories, definirBuscaStories] = useState("");
  const buscaStoriesAdiada = useDeferredValue(buscaStories);
  const [colecoesAbertas, definirColecoesAbertas] = useState(false);
  const [storiesAbertas, definirStoriesAbertas] = useState(false);
  const [usuarioIg, definirUsuarioIg] = useState("");
  const [senhaIg, definirSenhaIg] = useState("");
  const [codigo2fa, definirCodigo2fa] = useState("");
  const [desafio2faId, definirDesafio2faId] = useState<string | null>(null);
  const [erroConexao, definirErroConexao] = useState<string | null>(null);
  const importarSessao = useImportarSessaoInstagram();
  const [cookieIg, definirCookieIg] = useState("");
  const [tokenClickUp, definirTokenClickUp] = useState("");
  const [listaClickUp, definirListaClickUp] = useState("");
  const [erroClickUp, definirErroClickUp] = useState<string | null>(null);
  const [okClickUp, definirOkClickUp] = useState(false);
  const [erroSelecao, definirErroSelecao] = useState<string | null>(null);

  const lista = disponiveis.data ?? [];
  const listaSeguidos = contasSeguidas.data ?? [];
  const temCache = lista.length > 0 || disponiveis.isSuccess;
  const atualizandoEmFundo =
    (disponiveis.isFetching && temCache && !disponiveis.isPending) ||
    (contasSeguidas.isFetching &&
      listaSeguidos.length > 0 &&
      !contasSeguidas.isPending);

  useEffect(() => {
    if (instagramConectado) return;
    limparCacheColecoesDisponiveis();
    definirIds(new Set());
    definirHidratado(false);
    definirSujoLocal(false);
    definirIdsStories(new Set());
    definirHidratadoStories(false);
    definirSujoStoriesLocal(false);
  }, [instagramConectado]);

  useEffect(() => {
    if (!instagramConectado || !disponiveis.data || hidratado) return;
    definirIds(
      new Set(disponiveis.data.filter((c) => c.selecionada).map((c) => c.id)),
    );
    definirHidratado(true);
    definirSujoLocal(false);
  }, [disponiveis.data, hidratado, instagramConectado]);

  useEffect(() => {
    if (!instagramConectado) return;
    if (!disponiveis.isSuccess || !hidratado || sujoLocal) return;
    if (disponiveis.isFetching) return;
    definirIds(
      new Set(disponiveis.data.filter((c) => c.selecionada).map((c) => c.id)),
    );
  }, [
    disponiveis.isSuccess,
    disponiveis.data,
    disponiveis.isFetching,
    hidratado,
    sujoLocal,
    instagramConectado,
  ]);

  useEffect(() => {
    if (!instagramConectado || !contasSeguidas.data || hidratadoStories) return;
    definirIdsStories(
      new Set(
        contasSeguidas.data.filter((c) => c.selecionada).map((c) => c.id),
      ),
    );
    definirHidratadoStories(true);
    definirSujoStoriesLocal(false);
  }, [contasSeguidas.data, hidratadoStories, instagramConectado]);

  useEffect(() => {
    if (!instagramConectado) return;
    if (!contasSeguidas.isSuccess || !hidratadoStories || sujoStoriesLocal) {
      return;
    }
    if (contasSeguidas.isFetching) return;
    definirIdsStories(
      new Set(
        contasSeguidas.data.filter((c) => c.selecionada).map((c) => c.id),
      ),
    );
  }, [
    contasSeguidas.isSuccess,
    contasSeguidas.data,
    contasSeguidas.isFetching,
    hidratadoStories,
    sujoStoriesLocal,
    instagramConectado,
  ]);

  const sujo = useMemo(() => {
    if (!disponiveis.data || !hidratado) return false;
    const salvos = new Set(
      disponiveis.data.filter((c) => c.selecionada).map((c) => c.id),
    );
    if (salvos.size !== ids.size) return true;
    for (const id of ids) if (!salvos.has(id)) return true;
    return false;
  }, [disponiveis.data, ids, hidratado]);

  const sujoStories = useMemo(() => {
    if (!contasSeguidas.data || !hidratadoStories) return false;
    const salvos = new Set(
      contasSeguidas.data.filter((c) => c.selecionada).map((c) => c.id),
    );
    if (salvos.size !== idsStories.size) return true;
    for (const id of idsStories) if (!salvos.has(id)) return true;
    return false;
  }, [contasSeguidas.data, idsStories, hidratadoStories]);

  const selecionadas = useMemo(
    () => lista.filter((c) => ids.has(c.id)),
    [lista, ids],
  );
  const outras = useMemo(
    () => lista.filter((c) => !ids.has(c.id)),
    [lista, ids],
  );
  const contasFiltradas = useMemo(() => {
    const termo = buscaStoriesAdiada.trim().toLowerCase();
    if (!termo) return listaSeguidos;
    return listaSeguidos.filter(
      (conta) =>
        conta.username.toLowerCase().includes(termo) ||
        (conta.fullName ?? "").toLowerCase().includes(termo),
    );
  }, [listaSeguidos, buscaStoriesAdiada]);
  const storiesMarcadas = useMemo(
    () => listaSeguidos.filter((c) => idsStories.has(c.id)).length,
    [listaSeguidos, idsStories],
  );

  function alternar(id: string) {
    definirSujoLocal(true);
    definirIds((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  function alternarStory(id: string) {
    definirSujoStoriesLocal(true);
    definirIdsStories((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  function atualizarLista() {
    atualizarColecoes.mutate();
    atualizarSeguidos.mutate();
  }

  function salvarSelecoes() {
    definirErroSelecao(null);
    const tarefas: Promise<unknown>[] = [];
    if (sujo) {
      tarefas.push(
        salvar.mutateAsync([...ids]).then(() => definirSujoLocal(false)),
      );
    }
    if (sujoStories) {
      tarefas.push(
        salvarStories
          .mutateAsync([...idsStories])
          .then(() => definirSujoStoriesLocal(false)),
      );
    }
    if (tarefas.length === 0) return;
    void Promise.all(tarefas).catch((erro: unknown) => {
      definirErroSelecao(mensagemDoErro(erro));
    });
  }

  function limparFormularioConexao() {
    definirSenhaIg("");
    definirCodigo2fa("");
    definirDesafio2faId(null);
    definirErroConexao(null);
  }

  function conectarInstagram() {
    definirErroConexao(null);
    conectar.mutate(
      { username: usuarioIg, password: senhaIg },
      {
        onSuccess: (resposta) => {
          if (resposta.status === "precisa_2fa") {
            definirDesafio2faId(resposta.desafioId);
            definirCodigo2fa("");
            return;
          }
          limparFormularioConexao();
        },
        onError: (erro) => {
          definirErroConexao(mensagemDoErro(erro));
        },
      },
    );
  }

  function conectarPeloCookie() {
    definirErroConexao(null);
    const sessao = sessaoInstagramDoCookie(cookieIg);
    if (!sessao) {
      definirErroConexao(
        "Esse valor não parece o sessionid do Instagram. Copie de novo o valor inteiro do cookie.",
      );
      return;
    }
    importarSessao.mutate(sessao, {
      onSuccess: (resposta) => {
        definirCookieIg("");
        if (resposta.status !== "valida") {
          definirErroConexao(
            "O Instagram não aceitou esse cookie. Entre de novo no instagram.com e copie o sessionid atualizado.",
          );
        }
      },
      onError: (erro) => {
        definirErroConexao(mensagemDoErro(erro));
      },
    });
  }

  function enviarCodigo2fa() {
    if (!desafio2faId) return;
    definirErroConexao(null);
    confirmar2fa.mutate(
      { desafioId: desafio2faId, codigo: codigo2fa },
      {
        onSuccess: () => {
          limparFormularioConexao();
        },
        onError: (erro) => {
          definirErroConexao(mensagemDoErro(erro));
        },
      },
    );
  }

  const buscandoListas =
    disponiveis.isFetching ||
    atualizarColecoes.isPending ||
    contasSeguidas.isFetching ||
    atualizarSeguidos.isPending;
  const salvandoSelecao = salvar.isPending || salvarStories.isPending;
  const conectando = conectar.isPending || confirmar2fa.isPending;
  const clickup = integracoes.data?.clickup;
  const clickupPronto =
    clickup?.tokenConfigurado === true && clickup.listIdMascarado !== null;

  const statusAba = (id: AbaIntegracaoDaConta): "ok" | "alerta" | "neutro" => {
    if (id === "instagram") {
      if (instagramConectado) return "ok";
      if (sessaoInstagram.data?.username) return "alerta";
      return "neutro";
    }
    if (id === "clickup") return clickupPronto ? "ok" : "neutro";
    return "neutro";
  };

  const selecaoSuja = sujo || sujoStories;

  return (
    <>
      <div
        className={
          esconderNavegacao
            ? "w-full space-y-6"
            : "mx-auto flex w-full max-w-5xl flex-col gap-8 lg:flex-row lg:items-start lg:gap-10"
        }
      >
        {esconderNavegacao ? null : (
        <nav
          aria-label="Integrações da conta"
          className="flex shrink-0 gap-1 overflow-x-auto pb-1 lg:sticky lg:top-4 lg:w-56 lg:flex-col lg:overflow-visible lg:pb-0"
        >
          {ITENS_INTEGRACAO.map(({ id, rotulo, descricao, Icone }) => {
            const ativo = aba === id;
            const status = statusAba(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => selecionarAba(id)}
                aria-current={ativo ? "page" : undefined}
                className={cn(
                  "flex min-w-[10.5rem] cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-w-0 lg:w-full",
                  ativo
                    ? "border-border bg-accent text-accent-foreground"
                    : "border-transparent text-muted-foreground hover:border-border/60 hover:bg-muted/60 hover:text-foreground",
                )}
              >
                <Icone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="block text-sm font-semibold tracking-tight">
                      {rotulo}
                    </span>
                    {status === "ok" ? (
                      <span
                        className="size-1.5 shrink-0 rounded-full bg-success"
                        title="Conectado"
                        aria-label="Conectado"
                      />
                    ) : null}
                    {status === "alerta" ? (
                      <span
                        className="size-1.5 shrink-0 rounded-full bg-warning"
                        title="Atenção"
                        aria-label="Atenção"
                      />
                    ) : null}
                  </span>
                  <span className="mt-0.5 hidden text-xs leading-snug text-muted-foreground lg:block">
                    {descricao}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>
        )}

        <div className="min-w-0 flex-1 space-y-6">
{aba === "instagram" ? (
              <>
                <div
                  className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
                  role="note"
                >
                  Esta sessão é da{" "}
                  <strong className="font-medium text-foreground">
                    conta do estúdio
                  </strong>{" "}
                  (referências, coleções e stories). Para publicar no Instagram
                  do cliente, use{" "}
                  <button
                    type="button"
                    className="cursor-pointer font-medium text-foreground underline-offset-2 hover:underline"
                    onClick={() => {
                      window.location.hash = "configuracoes?secao=meta";
                    }}
                  >
                    Configurações do projeto → Redes do cliente
                  </button>
                  .
                </div>
                <Secao
                  titulo="Instagram do estúdio"
                  descricao="Login da equipe para sincronizar coleções, referências e stories. Não publica no Instagram do cliente."
                >
                  <PreviewPerfilInstagram sessao={sessaoInstagram.data} />
                  <dl className="mt-5 space-y-1.5 border-t border-border/60 pt-4 text-sm text-muted-foreground">
                    <div className="flex justify-between gap-4">
                      <dt>Última validação</dt>
                      <dd className="tabular-nums text-foreground">
                        {formatarDataHora(sessaoInstagram.data?.validadaEm)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt>Próximo refresh</dt>
                      <dd className="tabular-nums text-foreground">
                        {formatarDataHora(sessaoInstagram.data?.expiraEm)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt>Último login</dt>
                      <dd className="tabular-nums text-foreground">
                        {formatarDataHora(sessaoInstagram.data?.ultimoLoginEm)}
                      </dd>
                    </div>
                  </dl>
                  {sessaoInstagram.data?.ultimoErro ? (
                    <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm leading-relaxed text-destructive">
                      {sessaoInstagram.data.ultimoErro}
                    </p>
                  ) : null}
                  {sessaoInstagram.data?.status === "checkpoint" ? (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      Abra o Instagram no celular, confirme que é você e clique
                      em Validar sessão.
                    </p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Botao
                      variante="contorno"
                      onClick={() => validarSessao.mutate()}
                      disabled={validarSessao.isPending}
                      carregando={validarSessao.isPending}
                    >
                      <ShieldCheck className="size-4" aria-hidden="true" />
                      Validar sessão
                    </Botao>
                    <Botao
                      variante="contorno"
                      onClick={() => invalidarSessao.mutate()}
                      disabled={invalidarSessao.isPending}
                      carregando={invalidarSessao.isPending}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      Invalidar
                    </Botao>
                  </div>

                  {!instagramConectado ? (
                    <div className="mt-6 space-y-3 border-t border-border/60 pt-4">
                      <p className="text-sm font-semibold tracking-tight text-foreground">
                        Conectar conta
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Usuário e senha. Se pedir 2FA, use o código do
                        autenticador.
                      </p>
                      {desafio2faId ? (
                        <div className="space-y-3">
                          <CampoDeTexto
                            rotulo="Código 2FA"
                            value={codigo2fa}
                            onChange={(evento) =>
                              definirCodigo2fa(
                                evento.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 6),
                              )
                            }
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="000000"
                          />
                          <div className="flex flex-wrap gap-2">
                            <Botao
                              onClick={enviarCodigo2fa}
                              disabled={codigo2fa.length !== 6 || conectando}
                              carregando={confirmar2fa.isPending}
                            >
                              Confirmar código
                            </Botao>
                            <Botao
                              variante="contorno"
                              onClick={() => {
                                definirDesafio2faId(null);
                                definirCodigo2fa("");
                                definirErroConexao(null);
                              }}
                              disabled={conectando}
                            >
                              Voltar
                            </Botao>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <CampoDeTexto
                            rotulo="Usuário"
                            value={usuarioIg}
                            onChange={(evento) =>
                              definirUsuarioIg(evento.target.value)
                            }
                            autoComplete="username"
                            placeholder="seu.usuario"
                          />
                          <CampoDeTexto
                            rotulo="Senha"
                            type="password"
                            value={senhaIg}
                            onChange={(evento) =>
                              definirSenhaIg(evento.target.value)
                            }
                            autoComplete="current-password"
                          />
                          <Botao
                            onClick={conectarInstagram}
                            disabled={
                              !usuarioIg.trim() || !senhaIg || conectando
                            }
                            carregando={conectar.isPending}
                          >
                            Conectar Instagram
                          </Botao>
                        </div>
                      )}
                      {!desafio2faId ? (
                        <div className="space-y-3 border-t border-border/60 pt-4">
                          <div className="space-y-2">
                            <p className="text-sm font-semibold tracking-tight text-foreground">
                              Entrar com o navegador
                            </p>
                            <p className="text-sm leading-relaxed text-muted-foreground">
                              Use quando o login com senha falhar. Entre na
                              conta em instagram.com, abra as ferramentas do
                              navegador (F12), vá em Aplicativo, depois
                              Cookies, e copie o valor de sessionid.
                            </p>
                          </div>
                          <CampoDeTexto
                            rotulo="sessionid"
                            type="password"
                            value={cookieIg}
                            onChange={(evento) =>
                              definirCookieIg(evento.target.value)
                            }
                            autoComplete="off"
                            placeholder="Cole aqui o valor do cookie"
                          />
                          <Botao
                            variante="contorno"
                            onClick={conectarPeloCookie}
                            disabled={
                              !cookieIg.trim() ||
                              importarSessao.isPending ||
                              conectando
                            }
                            carregando={importarSessao.isPending}
                          >
                            Conectar com o navegador
                          </Botao>
                        </div>
                      ) : null}
                      {erroConexao ? (
                        <p
                          className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm leading-relaxed text-destructive"
                          role="alert"
                        >
                          {erroConexao}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </Secao>

              <Acordeao
                titulo="Coleções"
                descricao="O que entra na próxima sincronização de posts salvos."
                resumo={
                  !instagramConectado
                    ? "Conecte o Instagram para listar"
                    : `${selecionadas.length} selecionada${selecionadas.length === 1 ? "" : "s"}`
                }
                aberto={colecoesAbertas}
                aoAlternar={() => definirColecoesAbertas((v) => !v)}
              >
                {!instagramConectado ? (
                  <p className="rounded-lg border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground">
                    Conecte o Instagram acima para listar e escolher as coleções.
                  </p>
                ) : (
                  <>
                {disponiveis.isPending && lista.length === 0 ? (
                  <div
                    className="space-y-2"
                    role="status"
                    aria-label="Carregando"
                  >
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Esqueleto key={i} className="h-14 w-full rounded-lg" />
                    ))}
                  </div>
                ) : null}

                {disponiveis.isError && lista.length === 0 ? (
                  <EstadoDeErro
                    mensagem={mensagemDoErro(disponiveis.error)}
                    {...(requestIdDoErro(disponiveis.error)
                      ? {
                          codigo: requestIdDoErro(disponiveis.error) as string,
                        }
                      : {})}
                    aoTentarNovamente={atualizarLista}
                  />
                ) : null}

                {disponiveis.isSuccess && lista.length === 0 ? (
                  <EstadoVazio
                    icone={<FolderOpen />}
                    titulo="Nenhuma coleção encontrada"
                    descricao="Confira o login do Instagram e tente atualizar a lista."
                  />
                ) : null}

                {lista.length > 0 ? (
                  <div className="space-y-6">
                    {disponiveis.isFetching ? (
                      <p
                        className="text-sm text-muted-foreground"
                        role="status"
                      >
                        Buscando coleções novas no Instagram.
                      </p>
                    ) : null}
                    <GrupoDeColecoes
                      titulo="Selecionadas"
                      contagem={selecionadas.length}
                      itens={selecionadas}
                      ids={ids}
                      aoAlternar={alternar}
                      vazio="Nenhuma coleção marcada ainda."
                    />
                    <GrupoDeColecoes
                      titulo="Disponíveis"
                      contagem={outras.length}
                      itens={outras}
                      ids={ids}
                      aoAlternar={alternar}
                      vazio="Todas as coleções já estão selecionadas."
                    />
                  </div>
                ) : null}

                {salvar.isSuccess && !sujo ? (
                  <p className="text-sm text-muted-foreground" role="status">
                    Seleção salva. Na próxima sincronização, só essas coleções entram.
                  </p>
                ) : null}
                  </>
                )}
              </Acordeao>

              <Acordeao
                titulo="Stories"
                descricao="Contas que você segue. Marque de quem quer acompanhar stories."
                resumo={
                  !instagramConectado
                    ? "Conecte o Instagram para listar"
                    : `${storiesMarcadas} marcada${storiesMarcadas === 1 ? "" : "s"}`
                }
                aberto={storiesAbertas}
                aoAlternar={() => definirStoriesAbertas((v) => !v)}
              >
                {!instagramConectado ? (
                  <p className="rounded-lg border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground">
                    Conecte o Instagram acima para listar as contas que você segue.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <CampoDeTexto
                      rotulo="Buscar conta"
                      placeholder="Nome ou @usuário"
                      value={buscaStories}
                      onChange={(evento) =>
                        definirBuscaStories(evento.target.value)
                      }
                      dica={
                        storiesMarcadas > 0
                          ? `${storiesMarcadas} marcada${storiesMarcadas === 1 ? "" : "s"} para stories`
                          : "Nenhuma conta marcada ainda"
                      }
                    />

                    {contasSeguidas.isPending && listaSeguidos.length === 0 ? (
                      <div
                        className="space-y-2"
                        role="status"
                        aria-label="Carregando contas seguidas"
                      >
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Esqueleto
                            key={i}
                            className="h-14 w-full rounded-lg"
                          />
                        ))}
                      </div>
                    ) : null}

                    {contasSeguidas.isError && listaSeguidos.length === 0 ? (
                      <EstadoDeErro
                        mensagem={mensagemDoErro(contasSeguidas.error)}
                        {...(requestIdDoErro(contasSeguidas.error)
                          ? {
                              codigo: requestIdDoErro(
                                contasSeguidas.error,
                              ) as string,
                            }
                          : {})}
                        aoTentarNovamente={() => atualizarSeguidos.mutate()}
                      />
                    ) : null}

                    {contasSeguidas.isSuccess && listaSeguidos.length === 0 ? (
                      <EstadoVazio
                        icone={<Users />}
                        titulo="Nenhuma conta seguida"
                        descricao="Atualize a lista ou confira se a sessão do Instagram está válida."
                      />
                    ) : null}

                    {listaSeguidos.length > 0 &&
                    contasFiltradas.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground">
                        Nada para "{buscaStories.trim()}". Tente outro nome.
                      </p>
                    ) : null}

                    {contasFiltradas.length > 0 ? (
                      <ListaDeContasSeguidas
                        itens={contasFiltradas}
                        ids={idsStories}
                        aoAlternar={alternarStory}
                      />
                    ) : null}

                    {salvarStories.isSuccess && !sujoStories ? (
                      <p
                        className="text-sm text-muted-foreground"
                        role="status"
                      >
                        Contas de stories salvas.
                      </p>
                    ) : null}
                  </div>
                )}
              </Acordeao>

              <Secao
                titulo="Sincronização"
                descricao="Estado da última sincronização com o Instagram."
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Etiqueta
                    tom={
                      sincronizacao.data?.status === "error"
                        ? "perigo"
                        : sincronizacao.data?.status === "success"
                          ? "sucesso"
                          : sincronizacao.data?.running
                            ? "atencao"
                            : "secundario"
                    }
                  >
                    {rotuloStatus(
                      sincronizacao.data?.status,
                      sincronizacao.data?.running,
                    )}
                  </Etiqueta>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {sincronizacao.data?.message ??
                    "Ainda não houve sincronização nesta sessão do servidor."}
                </p>
              </Secao>
            </>
          ) : null}

          {aba === "clickup" ? (
            <Secao
              titulo="ClickUp"
              descricao="Token e lista ficam na sua conta de usuário (não no projeto). Cria tasks de referências e espelha comentários."
            >
              {integracoes.isPending ? (
                <div className="space-y-2" role="status" aria-label="Carregando">
                  <Esqueleto className="h-28 w-full rounded-xl" />
                </div>
              ) : null}

              {integracoes.isError ? (
                <EstadoDeErro
                  mensagem={mensagemDoErro(integracoes.error)}
                  {...(requestIdDoErro(integracoes.error)
                    ? {
                        codigo: requestIdDoErro(integracoes.error) as string,
                      }
                    : {})}
                  aoTentarNovamente={() => void integracoes.refetch()}
                />
              ) : null}

              {clickup ? (
                <div className="space-y-8">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={clickupPronto ? "successOutline" : "warningOutline"}
                      >
                        {clickupPronto ? "Conectado" : "Não configurado"}
                      </Badge>
                      {clickup.listIdMascarado ? (
                        <span className="text-xs text-muted-foreground">
                          Lista {clickup.listIdMascarado}
                        </span>
                      ) : null}
                    </div>

                    <form
                      className="space-y-3"
                      onSubmit={(evento) => {
                        evento.preventDefault();
                        definirErroClickUp(null);
                        definirOkClickUp(false);
                        const corpo: {
                          token?: string;
                          listId?: string;
                        } = {};
                        if (tokenClickUp.trim()) {
                          corpo.token = tokenClickUp.trim();
                        }
                        if (listaClickUp.trim()) {
                          corpo.listId = listaClickUp.trim();
                        }
                        if (!corpo.token && !corpo.listId) {
                          definirErroClickUp(
                            "Informe o token e/ou o ID da lista para salvar.",
                          );
                          return;
                        }
                        atualizarClickUp.mutate(corpo, {
                          onSuccess: () => {
                            definirTokenClickUp("");
                            definirListaClickUp("");
                            definirOkClickUp(true);
                          },
                          onError: (erro) => {
                            definirErroClickUp(mensagemDoErro(erro));
                          },
                        });
                      }}
                    >
                      <CampoDeTexto
                        rotulo="Token da API ClickUp"
                        type="password"
                        value={tokenClickUp}
                        onChange={(evento) =>
                          definirTokenClickUp(evento.target.value)
                        }
                        autoComplete="off"
                        placeholder={
                          clickup.tokenConfigurado
                            ? "•••••••• (deixe em branco para manter)"
                            : "pk_..."
                        }
                      />
                      <CampoDeTexto
                        rotulo="ID da lista"
                        value={listaClickUp}
                        onChange={(evento) =>
                          definirListaClickUp(evento.target.value)
                        }
                        autoComplete="off"
                        placeholder={
                          clickup.listIdMascarado
                            ? `Atual: ${clickup.listIdMascarado}`
                            : "Ex.: 901114368044"
                        }
                      />
                      <div className="flex flex-wrap gap-2">
                        <Botao
                          type="submit"
                          carregando={atualizarClickUp.isPending}
                          disabled={atualizarClickUp.isPending}
                        >
                          <Save className="size-4" aria-hidden="true" />
                          Salvar na minha conta
                        </Botao>
                        {clickup.tokenConfigurado ? (
                          <Botao
                            type="button"
                            variante="contorno"
                            disabled={atualizarClickUp.isPending}
                            onClick={() => {
                              definirErroClickUp(null);
                              definirOkClickUp(false);
                              atualizarClickUp.mutate(
                                { token: "" },
                                {
                                  onSuccess: () => definirOkClickUp(true),
                                  onError: (erro) =>
                                    definirErroClickUp(mensagemDoErro(erro)),
                                },
                              );
                            }}
                          >
                            Remover token
                          </Botao>
                        ) : null}
                      </div>
                      {erroClickUp ? (
                        <p
                          className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive"
                          role="alert"
                        >
                          {erroClickUp}
                        </p>
                      ) : null}
                      {okClickUp ? (
                        <p className="text-sm text-muted-foreground" role="status">
                          Credenciais da conta atualizadas.
                        </p>
                      ) : null}
                    </form>
                  </div>

                  <div className="border-t border-border/60 pt-6">
                    <p className="text-sm font-semibold tracking-tight text-foreground">
                      Status do servidor
                    </p>
                    <dl className="mt-3 space-y-2 text-sm">
                      <LinhaStatus
                        rotulo="Webhook"
                        ok={clickup.webhookSecretConfigurado}
                        detalhe={
                          clickup.webhookSecretConfigurado
                            ? "Configurado no servidor"
                            : "Não configurado no servidor"
                        }
                      />
                      <LinhaStatus
                        rotulo="Campos personalizados"
                        ok={clickup.camposCustomizados > 0}
                        detalhe={`${clickup.camposCustomizados} mapeados no servidor`}
                      />
                    </dl>
                  </div>
                </div>
              ) : null}
            </Secao>
          ) : null}

        </div>
      </div>

      {aba === "instagram" && selecaoSuja
        ? createPortal(
            <div
              className="fixed inset-x-0 bottom-0 border-t border-border/70 bg-background px-4 py-4"
              style={{ zIndex: "var(--rk-z-sticky)" }}
              role="status"
            >
              <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Há alterações em coleções ou stories ainda não salvas.
                  </p>
                  {erroSelecao ? (
                    <p className="text-sm text-destructive" role="alert">
                      {erroSelecao}
                    </p>
                  ) : null}
                </div>
                <Botao
                  onClick={salvarSelecoes}
                  disabled={salvandoSelecao}
                  carregando={salvandoSelecao}
                >
                  <Save className="size-4" aria-hidden="true" />
                  Salvar seleção
                </Botao>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function PreviewPerfilInstagram({
  sessao,
}: {
  readonly sessao: InstagramSessao | undefined;
}) {
  const username = sessao?.username?.trim() || null;
  const conectado = Boolean(username) && sessao?.status === "valida";
  const inicial = (username ?? "?").slice(0, 1).toUpperCase();
  const avatar = sessao?.profilePicUrl?.trim() || null;

  return (
    <div className="flex items-center gap-4">
      {avatar ? (
        <MidiaAutenticada
          src={avatar}
          className="size-16 shrink-0 rounded-full object-cover"
          fallback={
            <span
              className="grid size-16 shrink-0 place-items-center rounded-full bg-primary text-xl font-semibold tracking-tight text-primary-foreground"
              aria-hidden="true"
            >
              {inicial}
            </span>
          }
        />
      ) : (
        <span
          className="grid size-16 shrink-0 place-items-center rounded-full bg-primary text-xl font-semibold tracking-tight text-primary-foreground"
          aria-hidden="true"
        >
          {inicial}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-lg font-semibold tracking-tight text-foreground">
            {username ? `@${username}` : "Nenhuma conta conectada"}
          </p>
          <Etiqueta tom={tomSessaoInstagram(sessao?.status)}>
            {rotuloSessaoInstagram(sessao?.status)}
          </Etiqueta>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {conectado
            ? "Perfil ativo nesta conta. A biblioteca de referências é só sua."
            : "Conecte abaixo para sincronizar coleções nesta conta de usuário."}
        </p>
      </div>
    </div>
  );
}

function LinhaStatus({
  rotulo,
  ok,
  detalhe,
}: {
  readonly rotulo: string;
  readonly ok: boolean;
  readonly detalhe: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-border/50 pt-2 first:border-t-0 first:pt-0">
      <dt className="text-muted-foreground">{rotulo}</dt>
      <dd className="text-right">
        <span
          className={
            ok
              ? "block text-sm font-medium text-foreground"
              : "block text-sm font-medium text-destructive"
          }
        >
          {ok ? "Ok" : "Pendente"}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {detalhe}
        </span>
      </dd>
    </div>
  );
}

function Secao({
  titulo,
  descricao,
  children,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly children: ReactNode;
}) {
  return (
    <Card flat className="gap-0 py-0 shadow-none">
      <CardHeader className="border-b border-border/50 px-5 py-4 sm:px-6">
        <CardTitle className="text-base font-semibold tracking-tight">
          {titulo}
        </CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          {descricao}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-5 py-5 sm:px-6">{children}</CardContent>
    </Card>
  );
}

function Acordeao({
  titulo,
  descricao,
  resumo,
  aberto,
  aoAlternar,
  children,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly resumo: string;
  readonly aberto: boolean;
  readonly aoAlternar: () => void;
  readonly children: ReactNode;
}) {
  const idPainel = `acordeao-${titulo.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <Card flat className="gap-0 overflow-hidden py-0 shadow-none">
      <button
        type="button"
        id={`${idPainel}-titulo`}
        aria-expanded={aberto}
        aria-controls={idPainel}
        onClick={aoAlternar}
        className={cn(
          "flex w-full cursor-pointer items-start justify-between gap-3 px-5 py-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-6",
          aberto ? "border-b border-border/50" : "hover:bg-muted/40",
        )}
      >
        <span className="min-w-0 flex-1 space-y-1">
          <span className="block text-base font-semibold tracking-tight text-foreground">
            {titulo}
          </span>
          <span className="block text-sm leading-relaxed text-muted-foreground">
            {aberto ? descricao : resumo}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "mt-0.5 size-5 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
            aberto && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      {aberto ? (
        <div id={idPainel} className="space-y-4 px-5 py-5 sm:px-6">
          {children}
        </div>
      ) : null}
    </Card>
  );
}

function ListaDeContasSeguidas({
  itens,
  ids,
  aoAlternar,
}: {
  readonly itens: readonly ContaSeguida[];
  readonly ids: ReadonlySet<string>;
  readonly aoAlternar: (id: string) => void;
}) {
  return (
    <ul className="max-h-[28rem] overflow-y-auto overflow-x-hidden rounded-xl border border-border/70 bg-card">
      {itens.map((conta, indice) => {
        const marcada = ids.has(conta.id);
        const inicial = conta.username.slice(0, 1).toUpperCase();
        return (
          <li
            key={conta.id}
            className={indice > 0 ? "border-t border-border/60" : undefined}
          >
            <button
              type="button"
              onClick={() => aoAlternar(conta.id)}
              aria-pressed={marcada}
              className="flex w-full cursor-pointer items-center gap-3.5 px-4 py-3 text-left transition-colors duration-200 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={
                  marcada
                    ? "grid size-5 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground"
                    : "grid size-5 shrink-0 place-items-center rounded-md border border-border bg-background"
                }
                aria-hidden="true"
              >
                {marcada ? <Check className="size-3" /> : null}
              </span>
              {conta.profilePicUrl ? (
                <MidiaAutenticada
                  src={conta.profilePicUrl}
                  className="size-9 shrink-0 rounded-full object-cover"
                  fallback={
                    <span
                      className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                      aria-hidden="true"
                    >
                      {inicial}
                    </span>
                  }
                />
              ) : (
                <span
                  className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                  aria-hidden="true"
                >
                  {inicial}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-sm font-medium tracking-tight text-foreground">
                    @{conta.username}
                  </span>
                  {conta.isVerified ? (
                    <Etiqueta tom="secundario">Verificada</Etiqueta>
                  ) : null}
                  {conta.isPrivate ? (
                    <Etiqueta tom="secundario">Privada</Etiqueta>
                  ) : null}
                </span>
                {conta.fullName ? (
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {conta.fullName}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function GrupoDeColecoes({
  titulo,
  contagem,
  itens,
  ids,
  aoAlternar,
  vazio,
}: {
  readonly titulo: string;
  readonly contagem: number;
  readonly itens: readonly ColecaoDisponivel[];
  readonly ids: ReadonlySet<string>;
  readonly aoAlternar: (id: string) => void;
  readonly vazio: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 px-1">
        <h3 className="text-sm font-medium tracking-tight text-foreground">
          {titulo}
        </h3>
        <span className="text-xs tabular-nums text-muted-foreground">
          {contagem}
        </span>
      </div>
      {itens.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground">
          {vazio}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border/70 bg-card">
          {itens.map((colecao, indice) => {
            const marcada = ids.has(colecao.id);
            return (
              <li
                key={colecao.id}
                className={indice > 0 ? "border-t border-border/60" : undefined}
              >
                <button
                  type="button"
                  onClick={() => aoAlternar(colecao.id)}
                  aria-pressed={marcada}
                  className="flex w-full cursor-pointer items-center gap-3.5 px-4 py-3.5 text-left transition-colors duration-200 hover:bg-muted/50"
                >
                  <span
                    className={
                      marcada
                        ? "grid size-5 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground"
                        : "grid size-5 shrink-0 place-items-center rounded-md border border-border bg-background"
                    }
                    aria-hidden="true"
                  >
                    {marcada ? <Check className="size-3" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium tracking-tight text-foreground">
                      {colecao.nome || colecao.id}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {colecao.mediaCount !== null
                        ? `${colecao.mediaCount} itens`
                        : "Contagem indisponível"}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function rotuloStatus(
  status: string | undefined,
  running: boolean | undefined,
): string {
  if (running) return "Rodando";
  if (status === "success") return "Sucesso";
  if (status === "error") return "Erro";
  if (status === "idle") return "Parado";
  return "-";
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    if (erro.ehNaoAutorizado) {
      return "Token de acesso ausente ou inválido.";
    }
    return erro.message;
  }
  return erro instanceof Error ? erro.message : "Erro inesperado.";
}

function requestIdDoErro(erro: unknown): string | null {
  return erro instanceof ErroDaApi ? erro.requestId : null;
}

function tomSessaoInstagram(
  status: InstagramSessao["status"] | undefined,
): "sucesso" | "atencao" | "perigo" | "secundario" {
  switch (status) {
    case "valida":
      return "sucesso";
    case "expirada":
    case "ausente":
      return "atencao";
    case "checkpoint":
    case "bloqueada":
      return "perigo";
    default:
      return "secundario";
  }
}

function rotuloSessaoInstagram(
  status: InstagramSessao["status"] | undefined,
): string {
  switch (status) {
    case "valida":
      return "Válida";
    case "expirada":
      return "Expirada";
    case "checkpoint":
      return "Checkpoint";
    case "bloqueada":
      return "Bloqueada";
    case "ausente":
      return "Ausente";
    default:
      return "-";
  }
}

function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return "-";
  }
}
